import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { url } from "../../Config/Config";

type Section = "support" | "payments" | "pricing" | "careers" | null;

function Verticalsidebar() {
  const { pathname } = useLocation();

  const isActive = (...paths: string[]) =>
    paths.some((path) => {
      const base = url + path;
      return pathname === base || pathname.startsWith(base + "/");
    });

  const isHome = pathname === url || pathname === url + "/";

  const itemClass = (active: boolean) =>
    `sidebar-item${active ? " selected" : ""}`;
  const linkClass = (active: boolean, hasArrow = false) =>
    `sidebar-link waves-effect${hasArrow ? " has-arrow" : ""}${
      active ? " active" : ""
    }`;

  const handleMouseEnter = () => {
    const mainWrapper = document.getElementById("main-wrapper");
    if (mainWrapper) {
      mainWrapper.setAttribute("data-sidebartype", "full");
    }
  };

  const handleMouseLeave = () => {
    const mainWrapper = document.getElementById("main-wrapper");
    if (mainWrapper) {
      mainWrapper.setAttribute("data-sidebartype", "mini-sidebar");
    }
  };

  const organizationsActive = isHome || isActive("/organizations");
  const cmsActive = isActive("/cms");
  const usersActive = isActive("/brew-users");
  const verifyActive = isActive("/verify-questions");
  const contestsActive = isActive("/contests");
  const supportActive = isActive("/support_requests");
  const paymentsActive = isActive("/payments", "/promo", "/coupon");
  const pricingActive = isActive("/pricing");
  const careersActive = isActive("/careers", "/jobs", "/skills");
  const saleActive = isActive("/sale_inquiries");
  const rewardsActive = isActive("/rewards");

  const currentSection: Section = supportActive
    ? "support"
    : paymentsActive
    ? "payments"
    : pricingActive
    ? "pricing"
    : careersActive
    ? "careers"
    : null;

  const [openMenu, setOpenMenu] = useState<Section>(currentSection);

  useEffect(() => {
    setOpenMenu(currentSection);
  }, [currentSection]);

  const toggleMenu = (section: Section) =>
    setOpenMenu((prev) => (prev === section ? null : section));

  const submenuClass = (section: Section) =>
    `collapse first-level${openMenu === section ? " in" : ""}`;

  return (
    <>
      <aside
        className="left-sidebar"
        data-sidebarbg="skin3"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="scroll-sidebar">
          <nav className="sidebar-nav">
            <ul id="sidebarnav">
              <li className={itemClass(organizationsActive)}>
                <Link
                  to={url + "/organizations"}
                  className={linkClass(organizationsActive)}
                >
                  <i className="mdi mdi-creation"></i>
                  <span className="hide-menu">Organizations</span>
                </Link>
              </li>

              <li className={itemClass(cmsActive)}>
                <Link to={url + "/cms/pages"} className={linkClass(cmsActive)}>
                  <i className="mdi mdi-file-document"></i>
                  <span className="hide-menu">Content Pages</span>
                </Link>
              </li>

              <li className={itemClass(usersActive)}>
                <Link
                  to={url + "/brew-users"}
                  className={linkClass(usersActive)}
                >
                  <i className="fa-solid fa-users-rectangle"></i>
                  <span className="hide-menu">Brew-Users</span>
                </Link>
              </li>

              <li className={itemClass(verifyActive)}>
                <Link
                  to={url + "/verify-questions?page=1"}
                  className={linkClass(verifyActive)}
                >
                  <i className="mdi mdi-help"></i>
                  <span className="hide-menu">Verify Questions</span>
                </Link>
              </li>

              <li className={itemClass(contestsActive)}>
                <Link
                  to={url + "/contests"}
                  className={linkClass(contestsActive)}
                >
                  <i className="mdi mdi-clipboard-text"></i>
                  <span className="hide-menu">Assesments</span>
                </Link>
              </li>

              <li className={itemClass(supportActive)}>
                <a
                  href={url + "/support_requests/queries"}
                  role="button"
                  aria-expanded={openMenu === "support"}
                  className={linkClass(supportActive, true)}
                  onClick={(e) => {
                    e.preventDefault();
                    toggleMenu("support");
                  }}
                >
                  <i className="mdi mdi-av-timer" />
                  <span className="hide-menu">Support Requests</span>
                </a>

                <ul className={submenuClass("support")}>
                  <li className="sidebar-item">
                    <Link
                      to={url + "/support_requests/queries"}
                      className={linkClass(
                        isActive("/support_requests/queries")
                      )}
                    >
                      <i className="mdi mdi-book-multiple"></i>
                      <span className="hide-menu"> Queries </span>
                    </Link>
                  </li>
                  <li className="sidebar-item">
                    <Link
                      to={url + "/support_requests/newsletter"}
                      className={linkClass(
                        isActive("/support_requests/newsletter")
                      )}
                    >
                      <i className="mdi mdi-email-open-outline"></i>
                      <span className="hide-menu"> NewsLetters </span>
                    </Link>
                  </li>
                  <li className="sidebar-item">
                    <Link
                      to={url + "/support_requests/meetings"}
                      className={linkClass(
                        isActive("/support_requests/meetings")
                      )}
                    >
                      <i className="mdi mdi-account-multiple"></i>
                      <span className="hide-menu">Meetings Schedule</span>
                    </Link>
                  </li>
                </ul>
              </li>

              <li className={itemClass(paymentsActive)}>
                <a
                  href={url + "/payments/plans"}
                  role="button"
                  aria-expanded={openMenu === "payments"}
                  className={linkClass(paymentsActive, true)}
                  onClick={(e) => {
                    e.preventDefault();
                    toggleMenu("payments");
                  }}
                >
                  <i className="mdi mdi-cash-usd" />
                  <span className="hide-menu">Payments</span>
                </a>

                <ul className={submenuClass("payments")}>
                  <li className="sidebar-item">
                    <Link
                      to={url + "/payments/plans"}
                      className={linkClass(isActive("/payments/plans"))}
                    >
                      <i className="mdi mdi-chart-pie"></i>
                      <span className="hide-menu"> Plans </span>
                    </Link>
                  </li>
                  <li className="sidebar-item">
                    <Link
                      to={url + "/payments/promo"}
                      className={linkClass(
                        isActive("/payments/promo", "/promo")
                      )}
                    >
                      <i className="mdi mdi-sale"></i>
                      <span className="hide-menu"> Promo Codes </span>
                    </Link>
                  </li>
                  <li className="sidebar-item">
                    <Link
                      to={url + "/payments/coupons"}
                      className={linkClass(
                        isActive("/payments/coupons", "/coupon")
                      )}
                    >
                      <i className="mdi mdi-tag-multiple"></i>
                      <span className="hide-menu"> Coupon Codes </span>
                    </Link>
                  </li>
                  <li className="sidebar-item">
                    <Link
                      to={url + "/payments/service-charge"}
                      className={linkClass(isActive("/payments/service-charge"))}
                    >
                      <i className="mdi mdi-coin"></i>
                      <span className="hide-menu">Service Charges</span>
                    </Link>
                  </li>
                  <li className="sidebar-item">
                    <Link
                      to={url + "/payments/dlq-messages"}
                      className={linkClass(isActive("/payments/dlq-messages"))}
                    >
                      <i className="mdi mdi-message-alert"></i>
                      <span className="hide-menu">DLQ Messages</span>
                    </Link>
                  </li>
                  <li className="sidebar-item">
                    <Link
                      to={url + "/payments/grant-credits"}
                      className={linkClass(isActive("/payments/grant-credits"))}
                    >
                      <i className="mdi mdi-gift"></i>
                      <span className="hide-menu">Grant Credits Manually</span>
                    </Link>
                  </li>
                </ul>
              </li>

              <li className={itemClass(pricingActive)}>
                <a
                  href={url + "/pricing/feature-catalog"}
                  role="button"
                  aria-expanded={openMenu === "pricing"}
                  className={linkClass(pricingActive, true)}
                  onClick={(e) => {
                    e.preventDefault();
                    toggleMenu("pricing");
                  }}
                >
                  <i className="mdi mdi-cash-usd" />
                  <span className="hide-menu">Pricing</span>
                </a>

                <ul className={submenuClass("pricing")}>
                  <li className="sidebar-item">
                    <Link
                      to={url + "/pricing/feature-catalog"}
                      className={linkClass(isActive("/pricing/feature-catalog"))}
                    >
                      <i className="mdi mdi-format-list-bulleted-type"></i>
                      <span className="hide-menu"> Feature Catalog </span>
                    </Link>
                  </li>
                  <li className="sidebar-item">
                    <Link
                      to={url + "/pricing/calculator-presets"}
                      className={linkClass(
                        isActive("/pricing/calculator-presets")
                      )}
                    >
                      <i className="mdi mdi-calculator"></i>
                      <span className="hide-menu"> Calculator Presets </span>
                    </Link>
                  </li>
                  <li className="sidebar-item">
                    <Link
                      to={url + "/pricing/topup-presets"}
                      className={linkClass(isActive("/pricing/topup-presets"))}
                    >
                      <i className="mdi mdi-cash-multiple"></i>
                      <span className="hide-menu"> Top-up Presets </span>
                    </Link>
                  </li>
                  <li className="sidebar-item">
                    <Link
                      to={url + "/pricing/bonus-rules"}
                      className={linkClass(isActive("/pricing/bonus-rules"))}
                    >
                      <i className="mdi mdi-gift"></i>
                      <span className="hide-menu"> Bonus Rules </span>
                    </Link>
                  </li>
                  <li className="sidebar-item">
                    <Link
                      to={url + "/pricing/currency-credit-rates"}
                      className={linkClass(
                        isActive("/pricing/currency-credit-rates")
                      )}
                    >
                      <i className="mdi mdi-currency-usd"></i>
                      <span className="hide-menu"> Currency Credit Rates </span>
                    </Link>
                  </li>
                  <li className="sidebar-item">
                    <Link
                      to={url + "/pricing/charge-sync"}
                      className={linkClass(isActive("/pricing/charge-sync"))}
                    >
                      <i className="mdi mdi-sync"></i>
                      <span className="hide-menu"> Charge Sync </span>
                    </Link>
                  </li>
                </ul>
              </li>

              <li className={itemClass(careersActive)}>
                <a
                  href={url + "/careers/jobs"}
                  role="button"
                  aria-expanded={openMenu === "careers"}
                  className={linkClass(careersActive, true)}
                  onClick={(e) => {
                    e.preventDefault();
                    toggleMenu("careers");
                  }}
                >
                  <i className="mdi mdi-chart-line" />
                  <span className="hide-menu">Careers</span>
                </a>

                <ul className={submenuClass("careers")}>
                  <li className="sidebar-item">
                    <Link
                      to={url + "/careers/jobs"}
                      className={linkClass(isActive("/careers/jobs", "/jobs"))}
                    >
                      <i className="mdi mdi-briefcase"></i>
                      <span className="hide-menu"> Jobs </span>
                    </Link>
                  </li>
                  <li className="sidebar-item">
                    <Link
                      to={url + "/skills"}
                      className={linkClass(isActive("/skills"))}
                    >
                      <i className="mdi mdi-format-list-bulleted"></i>
                      <span className="hide-menu">Skills</span>
                    </Link>
                  </li>
                </ul>
              </li>

              <li className={itemClass(saleActive)}>
                <Link
                  to={url + "/sale_inquiries"}
                  className={linkClass(saleActive)}
                >
                  <i className="mdi mdi-creation"></i>
                  <span className="hide-menu">Sale Inquiry</span>
                </Link>
              </li>

              <li className={itemClass(rewardsActive)}>
                <Link to={url + "/rewards"} className={linkClass(rewardsActive)}>
                  <i className="mdi mdi-trophy-award"></i>
                  <span className="hide-menu">Manage Rewards</span>
                </Link>
              </li>
            </ul>
          </nav>
        </div>
      </aside>
    </>
  );
}

export default Verticalsidebar;
